import React, { memo } from "react";
import AuthSignInButton from "@/components/AuthSignInButton";
import { UnstyledButton, Text as MantineText } from "@mantine/core";
import { trackEvent } from "@/lib/client/analytics";
import HeartIcon from "@/icons/Heart";

import styles from "./styles.module.scss";

interface FavoriteButtonProps {
  isSignedIn: boolean;
  isFavorited: boolean;
  disableFavorites: boolean;
  isAnimating: boolean;
  handleFavorite: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
  favoriteCount: number;
  hideFavoriteCount: boolean;
}

const FavoriteButton = memo(
  ({
    isSignedIn,
    isFavorited,
    disableFavorites,
    isAnimating,
    handleFavorite,
    favoriteCount,
    hideFavoriteCount,
  }: FavoriteButtonProps) => {
    const buttonContent = (
      <>
        <HeartIcon selected={isFavorited} locked={!isSignedIn || disableFavorites} />
        {favoriteCount > 0 && !hideFavoriteCount && (
          <MantineText span className={styles.favoriteCount}>
            {favoriteCount}
          </MantineText>
        )}
      </>
    );

    if (!isSignedIn) {
      return (
        <AuthSignInButton>
          {/* eslint-disable-next-line no-restricted-syntax -- Clerk must clone a native element. */}
          <button
            className={styles.headerFavorite}
            onClick={(e) => {
              e.stopPropagation();
              try {
                trackEvent("sign_in_prompt_shown", "session", {
                  trigger: "favorite_button",
                  page: "search",
                });
              } catch {
                // Analytics must never affect sign-in
              }
            }}
            data-selected={false}
            data-locked={true}
            aria-label="sign in to favorite"
            title="SIGN IN TO FAVORITE ITEMS"
          >
            {buttonContent}
          </button>
        </AuthSignInButton>
      );
    }

    if (disableFavorites) {
      return (
        <UnstyledButton
          className={styles.headerFavorite}
          data-selected={isFavorited}
          data-locked={true}
          aria-label="Favorites disabled"
          title="Favorites disabled"
          disabled
        >
          {buttonContent}
        </UnstyledButton>
      );
    }

    return (
      <UnstyledButton
        className={styles.headerFavorite}
        onClick={handleFavorite}
        data-selected={isFavorited}
        data-locked={false}
        data-animating={isAnimating}
        aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
        title={isFavorited ? "Remove from favorites" : "Add to favorites"}
      >
        {buttonContent}
      </UnstyledButton>
    );
  }
);

FavoriteButton.displayName = "FavoriteButton";

export default FavoriteButton;
